/**
 * Shop type definitions for Idle Depths.
 *
 * Shops allow players to buy and sell items.
 * Each location has its own shops with different inventories.
 */

/**
 * Shop definition registered via content packs.
 */
export interface ShopDefinition {
  /** Unique shop identifier */
  id: string;

  /** Display name */
  name: string;

  /** Location this shop is in */
  locationId: string;

  /** Items available for purchase */
  inventory: ShopInventoryItem[];

  /** Multiplier for sell prices (0.6 = 60% of item value) */
  sellPriceMultiplier: number;

  /** Whether buyback is enabled for this shop */
  buybackEnabled: boolean;
}

/**
 * Item available in a shop's inventory.
 */
export interface ShopInventoryItem {
  /** References ItemDefinition.id */
  itemId: string;

  /** Stock limit (null = infinite) */
  stockLimit: number | null;

  /** Current stock (runtime tracking, not persisted) */
  currentStock?: number;

  /** Override the item's base value for this shop */
  priceOverride?: number;
}

/**
 * Shop transaction record.
 */
export interface ShopTransaction {
  /** Transaction type */
  type: 'buy' | 'sell' | 'buyback';

  /** Item that was traded */
  itemId: string;

  /** Quantity traded */
  quantity: number;

  /** Price per item */
  pricePerItem: number;

  /** Total transaction amount */
  totalPrice: number;

  /** When the transaction occurred (Unix timestamp ms) */
  timestamp: number;
}

/**
 * Buyback item for recovering accidentally sold items.
 */
export interface BuybackItem {
  /** Item that was sold */
  itemId: string;

  /** Quantity that was sold */
  quantity: number;

  /** Price it was sold for (per item) */
  soldPrice: number;

  /** When it was sold (Unix timestamp ms) */
  soldAt: number;
}

/**
 * Type guard for ShopDefinition.
 *
 * @param shop - Unknown value to check
 * @returns True if shop is a valid ShopDefinition
 */
export function isShopDefinition(shop: unknown): shop is ShopDefinition {
  return (
    typeof shop === 'object' &&
    shop !== null &&
    'id' in shop &&
    typeof (shop as ShopDefinition).id === 'string' &&
    'name' in shop &&
    typeof (shop as ShopDefinition).name === 'string' &&
    'locationId' in shop &&
    typeof (shop as ShopDefinition).locationId === 'string' &&
    'inventory' in shop &&
    Array.isArray((shop as ShopDefinition).inventory)
  );
}
